import { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid-premium';
import { Box, Chip, Typography } from '@mui/material';
import { OTCDatabaseRow } from './types';

interface UseOTCDatabaseColumnsProps {
  autosizeColumns: boolean;
  isGroupingEnabled?: boolean;
  dynamicColumns?: string[];
}

// Status color mapping
const getStatusColor = (
  status: string | null
): 'success' | 'warning' | 'error' | 'default' => {
  if (!status) return 'default';
  const lowerStatus = status.toLowerCase();
  if (lowerStatus === 'active' || lowerStatus === 'live') return 'success';
  if (lowerStatus === 'pending' || lowerStatus === 'draft') return 'warning';
  if (lowerStatus === 'inactive' || lowerStatus === 'deleted') return 'error';
  return 'default';
};

// Format dynamic column header names
const formatColumnHeader = (name: string): string => {
  if (name.toLowerCase() === 'isin') return 'ISIN';
  return name.charAt(0).toUpperCase() + name.slice(1);
};

export const useOTCDatabaseColumns = ({
  autosizeColumns,
  isGroupingEnabled = false,
  dynamicColumns = [],
}: UseOTCDatabaseColumnsProps) => {
  const columns: GridColDef[] = useMemo(() => {
    const cols: GridColDef[] = [
      // Dedicated group column - only shown when grouping is enabled
      ...(isGroupingEnabled
        ? [
            {
              field: 'group_name',
              headerName: 'Group',
              ...(autosizeColumns ? {} : { flex: 1.2 }),
              sortable: false,
              filterable: false,
              pinnable: false,
              hideable: false,
              editable: false,
              valueGetter: (value: any, row: OTCDatabaseRow) => {
                if (row.isGroup) {
                  return row.groupName;
                }
                return '';
              },
            },
          ]
        : []),
      {
        field: 'instrument_status',
        headerName: 'Status',
        ...(autosizeColumns ? {} : { flex: 0.8 }),
        minWidth: 100,
        editable: false,
        sortable: true,
        filterable: true,
        renderCell: params => {
          if (params.row.isGroup) return null;
          const status = params.value as string | null;
          if (!status) {
            return null;
          }
          return (
            <Chip
              label={status}
              color={getStatusColor(status)}
              size="small"
              variant="outlined"
            />
          );
        },
      },
      {
        field: 'instrument_type',
        headerName: 'Type',
        ...(autosizeColumns ? {} : { flex: 0.8 }),
        minWidth: 120,
        editable: false,
        sortable: true,
        filterable: true,
        renderCell: params => {
          if (params.row.isGroup) return null;
          return params.value;
        },
      },
      {
        field: 'name',
        headerName: 'Name',
        ...(autosizeColumns ? {} : { flex: 1.5 }),
        minWidth: 200,
        editable: false,
        sortable: true,
        filterable: true,
        renderCell: params => {
          return params.value;
        },
      },
      {
        field: 'code',
        headerName: 'Code',
        ...(autosizeColumns ? {} : { flex: 1 }),
        minWidth: 120,
        editable: false,
        sortable: true,
        filterable: true,
        renderCell: params => {
          return params.value;
        },
      },
      {
        field: 'country',
        headerName: 'Country',
        ...(autosizeColumns ? {} : { flex: 0.8 }),
        minWidth: 100,
        editable: false,
        sortable: true,
        filterable: true,
        renderCell: params => {
          return params.value;
        },
      },
      {
        field: 'currency',
        headerName: 'Currency',
        ...(autosizeColumns ? {} : { flex: 0.7 }),
        minWidth: 100,
        editable: false,
        sortable: true,
        filterable: true,
        renderCell: params => {
          return params.value;
        },
      },
      {
        field: 'multiplier',
        headerName: 'Multiplier',
        ...(autosizeColumns ? {} : { flex: 0.7 }),
        minWidth: 100,
        editable: true,
        sortable: true,
        filterable: true,
        type: 'number',
        valueSetter: (value, row) => {
          if (value === 0) {
            return row;
          }
          return { ...row, multiplier: value };
        },
        renderCell: params => {
          const isModified =
            params.row.original_multiplier != null &&
            params.row.multiplier !== params.row.original_multiplier;

          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {isModified && (
                <Typography
                  component="span"
                  sx={{
                    color: 'warning.main',
                    fontWeight: 'bold',
                    fontSize: 16,
                  }}
                  title="Manually modified"
                >
                  *
                </Typography>
              )}
              {params.value}
            </Box>
          );
        },
      },
      {
        field: 'effective_multiplier',
        headerName: 'Effective Multiplier',
        ...(autosizeColumns ? {} : { flex: 0.7 }),
        minWidth: 140,
        editable: false,
        sortable: true,
        filterable: true,
        type: 'number',
        renderCell: params => (
          <Typography sx={{ fontSize: '0.875rem' }}>
            {params.value ?? 'N/A'}
          </Typography>
        ),
      },
      {
        field: 'asset_sub_class',
        headerName: 'Sub Class',
        ...(autosizeColumns ? {} : { flex: 1 }),
        minWidth: 120,
        editable: false,
        sortable: true,
        filterable: true,
        renderCell: params => {
          return params.value;
        },
      },
      // Dynamic columns from wizard_column data
      ...dynamicColumns.map(
        (colName): GridColDef => ({
          field: `wizard_${colName}`,
          headerName: formatColumnHeader(colName),
          ...(autosizeColumns ? {} : { flex: 1 }),
          minWidth: 120,
          editable: false,
          sortable: true,
          filterable: true,
          renderCell: params => {
            return params.value;
          },
        })
      ),
    ];

    return cols;
  }, [autosizeColumns, isGroupingEnabled, dynamicColumns]);

  return {
    columns,
  };
};
